import { api } from '@/lib/api';
import type {
  NatsMessagePage,
  NatsOverview,
  NatsStreamDetail,
  RedisKeyPage,
  RedisOverview,
  RedisValue,
} from '@/types/inspect';
import type { UserDto } from '@/types/user';
import type {
  Alert,
  NoiseBudget,
  NotificationChannel,
  NotificationRule,
  Page,
  RunDetail,
  RunEvent,
  RunSummary,
  WorkflowDetail,
  WorkflowSummary,
} from '@/types/sturm';

export interface RunQuery {
  workflow?: string;
  status?: string;
  search?: string;
  cursor?: string;
  limit?: number;
}

export const meApi = {
  getMe: () => api.get<UserDto>('/me').then((r) => r.data),
};

export const workflowsApi = {
  list: () => api.get<WorkflowSummary[]>('/workflows').then((r) => r.data),
  get: (slug: string) => api.get<WorkflowDetail>(`/workflows/${slug}`).then((r) => r.data),
  runs: (slug: string, query: RunQuery = {}) =>
    api.get<Page<RunSummary>>(`/workflows/${slug}/runs`, { params: query }).then((r) => r.data),
  pause: (slug: string) => api.post(`/workflows/${slug}/pause`).then((r) => r.data),
  resume: (slug: string) => api.post(`/workflows/${slug}/resume`).then((r) => r.data),
  disable: (slug: string) => api.post<WorkflowDetail>(`/workflows/${slug}/disable`).then((r) => r.data),
  enable: (slug: string) => api.post<WorkflowDetail>(`/workflows/${slug}/enable`).then((r) => r.data),
  start: (slug: string, input: unknown, idempotencyKey: string, testMode: boolean) =>
    api
      .post<RunSummary>(`/workflows/${slug}/runs`, { input, idempotencyKey, testMode })
      .then((r) => r.data),
  updateSettings: (slug: string, patch: Record<string, unknown>) =>
    api.patch<WorkflowDetail>(`/workflows/${slug}/settings`, patch).then((r) => r.data),
};

export const runsApi = {
  list: (query: RunQuery = {}) =>
    api.get<Page<RunSummary>>('/runs', { params: query }).then((r) => r.data),
  get: (id: string) => api.get<RunDetail>(`/runs/${id}`).then((r) => r.data),
  events: (id: string, afterSeq?: number) =>
    api.get<RunEvent[]>(`/runs/${id}/events`, { params: { afterSeq } }).then((r) => r.data),
  retry: (id: string) => api.post<RunSummary>(`/runs/${id}/retry`).then((r) => r.data),
  retryStep: (id: string, stepName: string) =>
    api.post<RunSummary>(`/runs/${id}/steps/${stepName}/retry`).then((r) => r.data),
  rerun: (id: string) => api.post<RunSummary>(`/runs/${id}/rerun`).then((r) => r.data),
  signal: (id: string, name: string, payload: unknown) =>
    api.post(`/runs/${id}/signal`, { name, payload }).then((r) => r.data),
  cancel: (id: string) => api.post(`/runs/${id}/cancel`).then((r) => r.data),
  terminate: (id: string) => api.post(`/runs/${id}/terminate`).then((r) => r.data),
  exportUrl: (query: RunQuery = {}) => {
    const params = new URLSearchParams(
      Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([k, v]) => [k, String(v)]),
    );
    return `/runs/export?${params.toString()}`;
  },
};

export const searchApi = {
  query: (q: string) => api.get<RunSummary[]>('/search', { params: { q } }).then((r) => r.data),
};

export const notificationsApi = {
  rules: () => api.get<NotificationRule[]>('/notifications/rules').then((r) => r.data),
  createRule: (rule: Partial<NotificationRule>) =>
    api.post<NotificationRule>('/notifications/rules', rule).then((r) => r.data),
  updateRule: (id: string, rule: Partial<NotificationRule>) =>
    api.put<NotificationRule>(`/notifications/rules/${id}`, rule).then((r) => r.data),
  deleteRule: (id: string) => api.delete(`/notifications/rules/${id}`).then((r) => r.data),
  testRule: (id: string) => api.post(`/notifications/rules/${id}/test`).then((r) => r.data),
  channels: () => api.get<NotificationChannel[]>('/notifications/channels').then((r) => r.data),
  testChannel: (id: string) => api.post(`/notifications/channels/${id}/test`).then((r) => r.data),
  noiseBudget: () => api.get<NoiseBudget>('/notifications/noise-budget').then((r) => r.data),
};

export const alertsApi = {
  list: () => api.get<Alert[]>('/alerts').then((r) => r.data),
  acknowledge: (id: string) => api.post(`/alerts/${id}/acknowledge`).then((r) => r.data),
  mute: (id: string, duration = '1h') =>
    api.post(`/alerts/${id}/mute`, { duration }).then((r) => r.data),
  resolve: (id: string) => api.post(`/alerts/${id}/resolve`).then((r) => r.data),
};

export interface NatsPeekQuery {
  subject?: string;
  startSeq?: number;
  limit?: number;
}

export interface RedisScanQuery {
  match?: string;
  db?: number;
  cursor?: number;
  pageOffset?: number;
  limit?: number;
  memory?: boolean;
}

export const inspectNatsApi = {
  overview: () => api.get<NatsOverview>('/inspect/nats/overview').then((r) => r.data),
  stream: (name: string) =>
    api
      .get<NatsStreamDetail>(`/inspect/nats/streams/${encodeURIComponent(name)}`)
      .then((r) => r.data),
  messages: (name: string, query: NatsPeekQuery = {}) =>
    api
      .get<NatsMessagePage>(`/inspect/nats/streams/${encodeURIComponent(name)}/messages`, {
        params: query,
      })
      .then((r) => r.data),
};

export const inspectRedisApi = {
  overview: () => api.get<RedisOverview>('/inspect/redis/overview').then((r) => r.data),
  keys: (query: RedisScanQuery = {}) =>
    api.get<RedisKeyPage>('/inspect/redis/keys', { params: query }).then((r) => r.data),
  value: (key: string, db?: number) =>
    api.get<RedisValue>('/inspect/redis/value', { params: { key, db } }).then((r) => r.data),
};
